import { useQuery, useQueryClient, useQueries } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

import { useLoadingScreen } from '../../Store/LoadingManager';

import { LoginUserService, SignupService, ForgotPasswordService, VerifyPasswordService, Setup2FAService, Confirm2FAService, Disable2FAService, VerifyLogin2FAService, Recover2FAService, VerifyRecover2FAService } from "../../Api/Service/Authentication/authenticationService";
import { ResetPasswordAdminUserService } from "../../Api/Service/AdminUserControl/adminUserControlService";


import { Authentication } from "../../Models/Authentication/Authentication";

import LocalStorageHelper from "../../Utilities//Helpers/LocalStorageHelper";
import DropdownOptionsHelper from "../../Utilities/Helpers/DropdownOptionsHelper";

import { AuthenticationField } from "../../Utilities/Enums/ParametersEnum";
import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { CurrencyEnum } from '../../Utilities/Enums/CurrencyEnums';
import { RoleTypeEnum } from '../../Utilities/Enums/RolesEnums';

import { ErrorMessage, SuccessMessage } from "../../Constants/Messages";

export function useAuthenticationHook() {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    let { loading, setLoading } = useLoadingScreen();
    const DropdownList = [CachingEnum.ACCOUNT_TYPE_LIST, CachingEnum.PRICE_SETTINGS_TAB_LIST];

    // Initial fetch
    const { data: DropdownData, pending: dropdownFetchStatus } = useQueries({
        queries: DropdownList.map((item) => {
            return {
                queryKey: [item],
                queryFn: () => DropdownOptionsHelper.getLists(item),
            }
        }),
        combine: (results) => {
            const dictionary = Object.assign({}, ...results.map((x, index) => ({ [DropdownList[index]]: x.data })));
            return {
                data: dictionary,
                pending: results.some((result) => result.isPending),
            }
        },
    })

    const loginUser = async (params: any) => {
        // let _data;
        const responseData = await LoginUserService(params);

        if (responseData && responseData.isSuccess && responseData.isSuccess != "") {
            let userData: Authentication = {
                ...new Authentication(),
                ...responseData?.data?.data,
                [AuthenticationField.NAME]: responseData?.data?.data?.[AuthenticationField.NAME] ?? null,
                [AuthenticationField.USERNAME]: responseData?.data?.data?.[AuthenticationField.USERNAME] ?? null,
                [AuthenticationField.CURRENCY_CODE]: responseData?.data?.data?.[AuthenticationField.CURRENCY_CODE] ?? CurrencyEnum.PHP,
                [AuthenticationField.ROLE_TYPE]: responseData?.data?.data?.[AuthenticationField.ROLE_TYPE] ?? RoleTypeEnum.DEFAULT,
            };

            LocalStorageHelper.saveUserData(userData ?? '""');
            LocalStorageHelper.saveIsForgotPassword(false);
            LocalStorageHelper.saveUpdateUserProfile(true);
        }
        
        return responseData;
    }

    const signUpUser = async (params: any) => {
        setLoading(true);
        const responseData = await SignupService(params);

        if (responseData?.isSuccess) {
            enqueueSnackbar(responseData?.data?.message ?? "User " + SuccessMessage.CREATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_CREATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const forgotPassword = async (params: any) => {
        setLoading(true);
        const responseData = await ForgotPasswordService(params);

        if (responseData?.isSuccess) {
            enqueueSnackbar(responseData?.data?.message ?? SuccessMessage.TEMPORARY_PASSWORD_SUCCESSFULLY_SENT, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_SENDING_TEMPORARY_PASSWORD), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const verifyPassword = async (params: any) => {
        setLoading(true);
        const responseData = await VerifyPasswordService(params);

        if (responseData?.isSuccess) {
            enqueueSnackbar(responseData?.data?.message ?? "User " + SuccessMessage.PASSWORD_RESET_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_RESETTING_PASSWORD), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const resetPasswordById = async (id: any, params: any) => {
        setLoading(true);

        const responseData = await ResetPasswordAdminUserService(id, params);

        if (responseData?.isSuccess) {
            enqueueSnackbar(responseData?.data?.message ?? "Admin User " + SuccessMessage.PASSWORD_RESET_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_RESETTING_PASSWORD), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const setup2FA = async (params: any) => {
        setLoading(true);
        const responseData = await Setup2FAService(params);

        // if (responseData?.isSuccess) {
        //     enqueueSnackbar(responseData?.data?.message ?? "User " + SuccessMessage.CREATED_SUCCESSFULLY, { variant: 'success' })
        // } else {
        //     enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_CREATING_DATA), { variant: 'error' })
        // }

        setLoading(false);

        return responseData;
    }

    const confirm2FA = async (params: any) => {
        setLoading(true);
        const responseData = await Confirm2FAService(params);

        if (responseData?.isSuccess) {
            enqueueSnackbar(responseData?.data?.data?.message ?? "User " + SuccessMessage.TWOFA_ENABLED, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_TWOFA_ENABLED), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const disable2FA = async (params: any) => {
        setLoading(true);
        const responseData = await Disable2FAService(params);

        if (responseData?.isSuccess) {
            enqueueSnackbar(responseData?.data?.data?.message ?? "User " + SuccessMessage.TWOFA_DISABLED, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_TWOFA_DISABLED), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const verifyLogin2FA = async (params: any) => {
        setLoading(true);
        const responseData: any = await VerifyLogin2FAService(params);

        if (responseData && responseData.isSuccess && responseData.isSuccess != "") {
            let userData: Authentication = {
                ...new Authentication(),
                ...responseData?.data?.data,
                [AuthenticationField.NAME]: responseData?.data?.data?.[AuthenticationField.NAME] ?? null,
                [AuthenticationField.USERNAME]: responseData?.data?.data?.[AuthenticationField.USERNAME] ?? null,
                [AuthenticationField.CURRENCY_CODE]: responseData?.data?.data?.[AuthenticationField.CURRENCY_CODE] ?? CurrencyEnum.PHP,
                [AuthenticationField.ROLE_TYPE]: responseData?.data?.data?.[AuthenticationField.ROLE_TYPE] ?? RoleTypeEnum.DEFAULT,
            };

            LocalStorageHelper.saveUserData(userData ?? '""');
            LocalStorageHelper.saveIsForgotPassword(false);
            LocalStorageHelper.saveUpdateUserProfile(true);
        }

        setLoading(false);

        return responseData;
    }

    const recover2FA = async (params: any) => {
        setLoading(true);
        const responseData = await Recover2FAService(params);

        if (responseData?.isSuccess) {
            enqueueSnackbar(responseData?.data?.message ?? "User " + SuccessMessage.TWOFA_RECOVERY_CODE_EMAIL_SENT, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.message ?? ErrorMessage.PROBLEM_TWOFA_RECOVERY_CODE_EMAIL_SENT), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const verifyRecover2FA = async (params: any) => {
        setLoading(true);
        const responseData = await VerifyRecover2FAService(params);

        if (responseData?.isSuccess) {
            enqueueSnackbar(responseData?.data?.message ?? "User " + SuccessMessage.TWOFA_DISABLED, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_TWOFA_DISABLED), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    return { DropdownData, dropdownFetchStatus, loginUser, signUpUser, forgotPassword, verifyPassword, resetPasswordById, setup2FA, confirm2FA, disable2FA, verifyLogin2FA, recover2FA, verifyRecover2FA }

}